import { useEffect, useState } from 'react';
import { SSO_ENABLED, useAuth } from './AuthProvider';
import { SAME_ORIGIN_AUTH_ENABLED } from '../firebase';
import { shouldAttemptAutoSignIn } from './autoSignIn';
import { signInTitleFor } from './signInIdentity';

/**
 * The sign-in screen — which, with SSO on, most people should never see.
 *
 * IT ASKS FOR A CLICK, AND THAT IS NOT LAZINESS. Signing in automatically was tried and removed:
 * it needs `signInWithRedirect`, because a popup opened without a user gesture is blocked by every
 * browser — and the redirect never completes here. The SAML assertion is posted to the handler on
 * `aalrrnewclientintake.firebaseapp.com` while the app is served from `nmm.aalrr.com`, and handing
 * the session across those origins is a cross-site storage write that Edge blocks. People picked
 * their account at Microsoft and arrived back signed out.
 *
 * The popup behind this button is unaffected, because it hands the session back through
 * `window.opener` rather than storage. Making the automatic path work means pointing `authDomain`
 * at the serving host so the whole flow is same-origin, which also means registering a reply URL
 * per domain in Entra — worth doing deliberately, not as a side effect.
 *
 * ITS TITLE FOLLOWS THE DESTINATION. It used to say "New Matter Intake" to somebody who had
 * followed a link to the conflicts page, which reads like a wrong link at the one moment they are
 * least able to check.
 *
 * In development (no SAML provider configured) none of this applies and the seeded-account
 * shortcuts below are shown instead.
 */

/** Mirrors functions/scripts/seed.js. */
const SEEDED_ACCOUNTS = [
  { email: 'jason.tank@aalrr.com', displayName: 'Jason Tank', role: 'Admin' },
  { email: 'alice.attorney@aalrr.com', displayName: 'Alice Attorney', role: 'Attorney (submitter)' },
  { email: 'paula.pgl@aalrr.com', displayName: 'Paula Practice-Leader', role: 'PGL — Business & Tax' },
  { email: 'tom.teamlead@aalrr.com', displayName: 'Tom Team-Leader', role: 'Team Leader — Business & Tax' },
  { email: 'lena.litpgl@aalrr.com', displayName: 'Lena Litigation-Leader', role: 'PGL — Litigation' },
  { email: 'larry.litigator@aalrr.com', displayName: 'Larry Litigator', role: 'Attorney (litigation)' },
  { email: 'marcus.mp@aalrr.com', displayName: 'Marcus Managing-Partner', role: 'Managing Partner' },
  { email: 'gina.gc@aalrr.com', displayName: 'Gina General-Counsel', role: 'General Counsel' },
];

export default function SignIn() {
  const { signIn, signInSilently, error } = useAuth();
  const [email, setEmail] = useState('alice.attorney@aalrr.com');
  const [busy, setBusy] = useState(false);

  // Only while the browser is on its way to Microsoft. The card is not rendered then: offering a
  // sign-in button to somebody already being signed in invites a second, competing attempt.
  const [redirecting, setRedirecting] = useState(false);

  const title = signInTitleFor(typeof window === 'undefined' ? '/' : window.location.pathname);

  useEffect(() => {
    if (
      !shouldAttemptAutoSignIn({
        ssoEnabled: SSO_ENABLED,
        sameOrigin: SAME_ORIGIN_AUTH_ENABLED,
        hasError: Boolean(error),
      })
    ) {
      return;
    }
    setRedirecting(true);
    signInSilently().catch(() => setRedirecting(false));
    // Once per mount; `error` is read rather than depended on, so returning with one does not
    // re-enter this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (evt, overrideEmail) => {
    evt?.preventDefault();
    const target = overrideEmail || email;
    setBusy(true);
    try {
      await signIn({
        email: target,
        password: 'password',
        displayName: SEEDED_ACCOUNTS.find((a) => a.email === target)?.displayName,
      });
    } catch {
      /* surfaced through context error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-100 dark:bg-slate-800 p-6">
      <div className="w-full max-w-xl rounded-lg bg-white dark:bg-slate-900 p-8 shadow">
        <h1 className="text-2xl font-semibold text-firm-700 dark:text-firm-100">{title}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Atkinson, Andelson, Loya, Ruud &amp; Romo
        </p>

        {SSO_ENABLED && redirecting ? (
          <p className="mt-8 rounded bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Signing you in…
          </p>
        ) : SSO_ENABLED ? (
          <>
            <p className="mt-6 text-sm text-slate-600 dark:text-slate-300">
              {error ? 'We could not sign you in automatically.' : 'Sign in with your AALRR account to continue.'}
            </p>
            {/*
              The actual reason, not just that there was one. "We could not sign you in" sends
              somebody to ask a question the message itself could answer — working out why this
              failed the first time meant reading a console over somebody's shoulder.
            */}
            {error && (
              <p className="mt-1 break-words text-xs text-slate-500 dark:text-slate-400">{error}</p>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="mt-3 w-full rounded bg-firm-700 dark:bg-firm-500 px-4 py-3 font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
            >
              Sign in with AALRR
            </button>
          </>
        ) : (
          <>
            <div className="mt-6 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 p-3 text-sm text-amber-900 dark:text-amber-100">
              <strong>Temporary development sign-in.</strong> This screen exists only so the
              emulator is usable. It is replaced by Entra ID SAML SSO before launch — no real
              passwords are involved and every account below uses{' '}
              <code className="rounded bg-amber-100 dark:bg-amber-900 px-1">password</code>.
            </div>

            <form onSubmit={submit} className="mt-6 flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 rounded border border-slate-300 dark:border-slate-600 px-3 py-2"
                placeholder="you@aalrr.com"
              />
              <button
                type="submit"
                disabled={busy}
                className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
              >
                Sign in
              </button>
            </form>

            <p className="mt-6 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Seeded accounts
            </p>
            <ul className="mt-2 divide-y divide-slate-100 rounded border border-slate-200 dark:border-slate-700">
              {SEEDED_ACCOUNTS.map((account) => (
                <li key={account.email}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={(e) => submit(e, account.email)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                  >
                    <span className="font-medium text-slate-800 dark:text-slate-100">{account.displayName}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{account.role}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {error && (
          <p className="mt-4 rounded bg-red-50 dark:bg-red-950 p-3 text-sm text-red-700 dark:text-red-300">{error}</p>
        )}

        {/*
          For somebody who is not in the firm and cannot get past this screen.
          A candidate sent a conflict-disclosure link may well end up here: by opening the bare
          domain, by a mail client mangling the link, or by bookmarking the site rather than the
          link. Without this they are looking at a firm sign-in button they can never satisfy,
          with nothing telling them so — and if the link is on a firm domain like forms.aalrr.com
          this is the first thing they see. Shown to everyone rather than guessed at by hostname,
          because it costs staff one line of small print and guessing wrong costs a candidate the
          whole form.
        */}
        <p className="mt-6 border-t border-slate-200 pt-4 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
          <strong className="text-slate-600 dark:text-slate-300">Completing a form we sent you?</strong>{' '}
          You do not need an account here, and this sign-in is only for the firm. Open the link in
          the email we sent you — it takes you straight to your form. If the link has stopped
          working, reply to that email and we will send a new one.
        </p>
      </div>
    </div>
  );
}
